import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, X, Clock, Check, CheckCheck } from 'lucide-react';
import { useChatRooms } from '@/shared/hooks/useChatRooms';
import { useChatMessages } from '@/shared/hooks/useChatMessages';
import { useAuth } from '@/shared/hooks/useAuth';
import { cn } from '@/shared/lib/utils';
import { Timestamp } from 'firebase/firestore';

const ChatPage: React.FC = () => {
    const { user } = useAuth();
    const { chatRooms, loading: roomsLoading, markAsRead, createChatRoom } = useChatRooms('user', user?.uid);
    const [selectedRoom, setSelectedRoom] = useState<string | null>(null);
    const [messageText, setMessageText] = useState('');
    const [sending, setSending] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const { messages, loading: messagesLoading, sendMessage } = useChatMessages(selectedRoom || undefined);

    const selectedChatRoom = chatRooms.find(room => room.id === selectedRoom);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Mark messages as read when room is opened
    useEffect(() => {
        if (selectedRoom && selectedChatRoom && selectedChatRoom.unreadCountUser > 0) {
            markAsRead(selectedRoom, 'user');
        }
    }, [selectedRoom, selectedChatRoom]);

    const handleStartChat = async () => {
        if (!user) return;
        setSending(true);
        const result = await createChatRoom(
            user.uid,
            user.displayName || user.email?.split('@')[0] || '사용자',
            user.email || '',
            'user'
        );

        if (result.success && result.roomId) {
            setSelectedRoom(result.roomId);
        } else {
            alert('채팅방 생성에 실패했습니다.');
        }
        setSending(false);
    };

    const handleSendMessage = async () => {
        if (!messageText.trim() || !selectedRoom || !user) return;

        setSending(true);
        const result = await sendMessage(
            selectedRoom,
            messageText,
            user.uid,
            user.displayName || user.email?.split('@')[0] || '사용자',
            'user'
        );

        if (result.success) {
            setMessageText('');
        } else {
            alert('메시지 전송에 실패했습니다.');
        }
        setSending(false);
    };

    const formatTime = (timestamp?: Timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate();
        const now = new Date();
        const diff = now.getTime() - date.getTime();

        if (diff < 60000) return '방금 전';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}분 전`;
        if (diff < 86400000) return date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
        return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
    };

    if (roomsLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-slate-400">로딩 중...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pb-24">
            <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
                <div className="space-y-2">
                    <h1 className="text-3xl font-black text-white uppercase tracking-tight">실시간 채팅</h1>
                    <p className="text-sm text-slate-400 font-bold">관리자와 실시간으로 대화하세요</p>
                </div>

                <div className="grid lg:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
                    {/* Chat Rooms List */}
                    <div className="lg:col-span-1 bg-slate-950/50 border border-slate-800 rounded-3xl overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-slate-800 bg-slate-900/30">
                            <h2 className="text-lg font-black text-white uppercase">대화 목록</h2>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {chatRooms.length === 0 ? (
                                <div className="p-12 text-center space-y-6">
                                    <div className="w-20 h-20 rounded-full bg-slate-900 mx-auto flex items-center justify-center">
                                        <MessageSquare className="h-10 w-10 text-slate-700" />
                                    </div>
                                    <div className="space-y-2">
                                        <p className="text-slate-400 font-black text-sm uppercase">활성 채팅이 없습니다</p>
                                        <p className="text-slate-600 text-[10px] font-bold uppercase tracking-widest leading-relaxed">
                                            도움이 필요하신가요?<br />
                                            전문 상담팀과 대화를 시작하세요.
                                        </p>
                                    </div>
                                    <button
                                        onClick={handleStartChat}
                                        disabled={sending}
                                        className="w-full h-14 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 group shadow-xl hover:shadow-emerald-500/20"
                                    >
                                        <MessageSquare className="h-4 w-4 group-hover:scale-110 transition-transform" />
                                        {sending ? '연결 중...' : '상담 시작하기'}
                                    </button>
                                </div>
                            ) : (
                                chatRooms.map((room) => (
                                    <div
                                        key={room.id}
                                        onClick={() => setSelectedRoom(room.id)}
                                        className={cn(
                                            "p-6 border-b border-slate-800/50 cursor-pointer transition-all hover:bg-slate-800/30",
                                            selectedRoom === room.id && "bg-emerald-500/10 border-l-4 border-l-emerald-500"
                                        )}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="text-sm font-black text-white truncate">
                                                        {room.adminName || '관리자'}
                                                    </h3>
                                                    {room.unreadCountUser > 0 && (
                                                        <span className="h-5 w-5 rounded-full bg-emerald-500 text-white text-[10px] font-black flex items-center justify-center">
                                                            {room.unreadCountUser}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-slate-400 truncate">{room.lastMessage || '메시지 없음'}</p>
                                            </div>
                                            <span className="text-[10px] text-slate-500 font-bold whitespace-nowrap">
                                                {formatTime(room.lastMessageTime)}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Chat Messages */}
                    <div className="lg:col-span-2 bg-slate-950/50 border border-slate-800 rounded-3xl overflow-hidden flex flex-col">
                        {selectedRoom ? (
                            <>
                                {/* Chat Header */}
                                <div className="p-6 border-b border-slate-800 bg-slate-900/30 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                                            <MessageSquare className="h-5 w-5 text-emerald-500" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-black text-white">{selectedChatRoom?.adminName || '관리자'}</h2>
                                            <p className="text-xs text-slate-500 font-bold">온라인</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setSelectedRoom(null)}
                                        className="h-8 w-8 rounded-lg hover:bg-slate-800 flex items-center justify-center text-slate-400"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>

                                {/* Messages */}
                                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                                    {messagesLoading ? (
                                        <div className="flex items-center justify-center h-full">
                                            <div className="text-slate-400">로딩 중...</div>
                                        </div>
                                    ) : messages.length === 0 ? (
                                        <div className="flex items-center justify-center h-full">
                                            <div className="text-center">
                                                <MessageSquare className="h-12 w-12 mx-auto mb-4 text-slate-700" />
                                                <p className="text-slate-500 font-bold text-sm">메시지를 시작하세요</p>
                                            </div>
                                        </div>
                                    ) : (
                                        messages.map((msg) => {
                                            const isMyMessage = msg.senderRole === 'user';
                                            const isRead = msg.readBy.length > 1; // More than just sender

                                            return (
                                                <div
                                                    key={msg.id}
                                                    className={cn(
                                                        "flex gap-3",
                                                        isMyMessage ? "justify-end" : "justify-start"
                                                    )}
                                                >
                                                    <div className={cn(
                                                        "max-w-[70%] space-y-1",
                                                        isMyMessage && "items-end"
                                                    )}>
                                                        <div className={cn(
                                                            "px-4 py-3 rounded-2xl",
                                                            isMyMessage
                                                                ? "bg-emerald-600 text-white"
                                                                : "bg-slate-800 text-white"
                                                        )}>
                                                            <p className="text-sm font-medium whitespace-pre-wrap break-words">
                                                                {msg.message}
                                                            </p>
                                                        </div>
                                                        <div className="flex items-center gap-1 px-2">
                                                            <span className="text-[10px] text-slate-500 font-bold">
                                                                {formatTime(msg.createdAt)}
                                                            </span>
                                                            {isMyMessage && (
                                                                isRead ?
                                                                    <CheckCheck className="h-3 w-3 text-emerald-500" /> :
                                                                    <Check className="h-3 w-3 text-slate-500" />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>

                                {/* Input */}
                                <div className="p-6 border-t border-slate-800 bg-slate-900/30">
                                    <div className="flex gap-3">
                                        <input
                                            type="text"
                                            value={messageText}
                                            onChange={(e) => setMessageText(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && !sending && handleSendMessage()}
                                            placeholder="메시지를 입력하세요..."
                                            className="flex-1 h-12 bg-slate-950 border border-slate-800 rounded-xl px-4 text-sm text-white outline-none focus:border-emerald-500/50 font-medium"
                                        />
                                        <button
                                            onClick={handleSendMessage}
                                            disabled={sending || !messageText.trim()}
                                            className="h-12 w-12 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Send className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-center">
                                    <MessageSquare className="h-16 w-16 mx-auto mb-4 text-slate-700" />
                                    <p className="text-slate-400 font-bold">대화를 선택하세요</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChatPage;
